import { useEffect } from 'react';
// Inline SVG speech-bubble cursor — small, white-filled with a dark stroke so
// it reads on both satellite and terrain basemaps. Hotspot at the bubble tail
// (bottom-left) where the user expects the pin to land.
const COMMENT_CURSOR_SVG = "data:image/svg+xml;utf8," +
    encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">' +
        '<path d="M3 28 L8 22 L22 22 A6 6 0 0 0 28 16 L28 10 A6 6 0 0 0 22 4 L10 4 A6 6 0 0 0 4 10 L4 22 Z" ' +
        'fill="#ffffff" stroke="#181818" stroke-width="2" stroke-linejoin="round"/>' +
        '<circle cx="11" cy="13" r="1.5" fill="#181818"/>' +
        '<circle cx="16" cy="13" r="1.5" fill="#181818"/>' +
        '<circle cx="21" cy="13" r="1.5" fill="#181818"/>' +
        "</svg>");
const COMMENT_CURSOR = `url("${COMMENT_CURSOR_SVG}") 3 28, crosshair`;
export function useCursorManager(viewer, toolStates) {
    useEffect(() => {
        if (!viewer || viewer.isDestroyed())
            return;
        const canvas = viewer.canvas;
        // 1. Dragging — pointer cursor
        if (toolStates.pathsDragging || toolStates.pathsDraggingPending || toolStates.measureDragging ||
            toolStates.placemarksDragging || toolStates.labelsDragging || toolStates.polygonDragging || toolStates.polygonDrawingDragging) {
            canvas.style.cursor = 'pointer';
            return;
        }
        // 2a. Hovering a polygon body — move cursor (whole-polygon drag), shown
        //     before the generic draggable pointer so the body reads as movable.
        if (toolStates.markupIsActive && toolStates.hoveredPolygonBody) {
            canvas.style.cursor = 'move';
            return;
        }
        // 2b. Hovering over draggable (vertex/handle) — pointer cursor
        if (toolStates.markupIsActive && toolStates.hoveredDraggable) {
            canvas.style.cursor = 'pointer';
            return;
        }
        // 3a. Comment placement — speech-bubble cursor (custom). Runs before the
        //     generic crosshair so the bubble glyph signals "you're leaving a comment".
        if (toolStates.isCommentToolActive) {
            canvas.style.cursor = COMMENT_CURSOR;
            return;
        }
        // 3b. Placement mode — crosshair
        if (toolStates.isPlacemarkToolActive || toolStates.isMeasureToolActive || toolStates.isPathsToolActive ||
            toolStates.isPolygonToolActive || toolStates.isLabelToolActive || toolStates.pathsExtending) {
            canvas.style.cursor = 'crosshair';
            return;
        }
        // 4. Highlight mode — crosshair when hovering a contour/stream
        //    entity. Two ways to qualify:
        //    (a) Highlight Mode is on, and the cursor is over any
        //        contour/stream — signals "click to highlight this".
        //    (b) The hovered contour/stream is *already highlighted* —
        //        signals "click to edit this highlight". This works even
        //        with Highlight Mode off, so existing highlights stay
        //        clickable from any state.
        const hoveredHighlight = (toolStates.hoveredContour && toolStates.hoveredContour.isHighlighted)
            || (toolStates.hoveredStream && toolStates.hoveredStream.isHighlighted);
        if ((toolStates.contourHighlightModeEnabled && toolStates.hoveredContour) ||
            (toolStates.streamHighlightModeEnabled && toolStates.hoveredStream) ||
            hoveredHighlight) {
            canvas.style.cursor = 'crosshair';
            return;
        }
        // 5. Default
        canvas.style.cursor = '';
    }, [viewer, toolStates.markupIsActive, toolStates.hoveredDraggable, toolStates.hoveredPolygonBody,
        toolStates.pathsDragging, toolStates.pathsDraggingPending, toolStates.pathsExtending,
        toolStates.measureDragging, toolStates.placemarksDragging, toolStates.labelsDragging,
        toolStates.polygonDragging, toolStates.polygonDrawingDragging,
        toolStates.isPlacemarkToolActive, toolStates.isMeasureToolActive, toolStates.isPathsToolActive,
        toolStates.isPolygonToolActive, toolStates.isLabelToolActive, toolStates.isCommentToolActive,
        toolStates.contourHighlightModeEnabled, toolStates.streamHighlightModeEnabled,
        toolStates.hoveredContour, toolStates.hoveredStream]);
}
